import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";

import { searchOtStaff } from "../api/otApi";

/**
 * Pick a member of staff, or type a name the directory does not have.
 *
 * freeSolo deliberately: a locum or a visiting surgeon has to be bookable on
 * the day, and refusing a case because the directory is behind would be worse
 * than a name-only row. A name-only row is not lost -- the PAC queue lists it
 * under "Not assigned" rather than dropping it.
 *
 * member_type is DOCTOR for both roles. The staff directory classifies people
 * as DOCTOR or NURSE and does not record who is an anaesthetist, so narrowing
 * further would mean guessing at a specialisation string.
 *
 * `role`, when given, is passed straight through to searchOtStaff as
 * `role=<key>` -- the server then narrows the result to staff on that roster
 * (e.g. `role="anaesthetist"` for the anaesthetist field, read from
 * main/ot/sql/036_ot_anaesthetist.sql). An unconfigured roster is searched
 * unfiltered rather than emptied; a roster the server cannot read fails
 * closed instead of falling back to everyone -- see that table's helper,
 * ot_common.is_anaesthetist, for why the two are not the same thing.
 */
export default function StaffPicker({ label, valueName, memberType = "DOCTOR", role, onPick }) {
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState(valueName || "");

  // Resync when the form is reset underneath us. OtRequestSection stays mounted
  // and blanks its form after a submit, so without this the picker kept showing
  // the previous anaesthetist while the form held nothing -- and the next
  // request went out with a name on screen and no anaesthetist sent. A no-op
  // while the user types, because onPick keeps valueName in lockstep with input.
  useEffect(() => setInput(valueName || ""), [valueName]);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchOtStaff({ q, member_type: memberType, role })
        .then((rows) => {
          if (!cancelled) setOptions(rows || []);
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input, memberType, role]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      // The server has already matched; filtering again here would hide rows
      // whose match was on the specialisation rather than the name.
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.member_name || "")}
      renderOption={(props, o) => (
        <li {...props} key={o.member_id}>
          {o.member_name}
          {o.detail ? ` — ${o.detail}` : ""}
        </li>
      )}
      inputValue={input}
      onInputChange={(_e, value, reason) => {
        setInput(value);
        // "input" is the user typing. Selecting or clearing arrives on
        // onChange instead, and must not be reported twice.
        if (reason === "input") onPick(value);
      }}
      onChange={(_e, value) => {
        if (value && typeof value === "object") setInput(value.member_name || "");
        onPick(value);
      }}
      renderInput={(params) => (
        <TextField {...params} fullWidth size="small" placeholder={label} />
      )}
    />
  );
}
